import React, { useState, useMemo } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import { EnhancedDeal, FrameType } from '../types';
import AppHeader from '../components/ui/AppHeader';
import ProjectBoard from '../components/projects/ProjectBoard';
import GanttChart from '../components/projects/GanttChart';
import ConvertToProjectModal from '../components/projects/ConvertToProjectModal';
import { Home, Package } from 'lucide-react';
import LoadingScreen from '../components/ui/LoadingScreen';
import './ProjectsPage.css';

type ViewMode = 'board' | 'gantt' | 'list';

const ProjectsPage: React.FC = () => {
  const { user } = useAuth();
  const { timberProjects, projectTasks, milestones, enhancedDeals, loading } = useCRM();
  useCRMCollections(['projectTasks', 'milestones']);
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [frameTypeFilter, setFrameTypeFilter] = useState<FrameType | 'all'>('all');

  // Get won deals that can be converted to projects
  const wonDeals = useMemo((): EnhancedDeal[] => {
    return enhancedDeals.filter(d => 
      d.outcome === 'won' &&
      !d.plHistoricalShell &&
      !timberProjects.some(p => p.dealId === d.id)
    );
  }, [enhancedDeals, timberProjects]);

  // Filter projects by status and frame type
  const filteredProjects = useMemo(() => {
    return timberProjects.filter(p => {
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const matchesFrameType = frameTypeFilter === 'all' || (p.frameType || 'custom') === frameTypeFilter;
      return matchesStatus && matchesFrameType;
    });
  }, [timberProjects, statusFilter, frameTypeFilter]);

  // Get tasks and milestones for selected project
  const selectedProjectTasks = useMemo(() => {
    if (!selectedProjectId) return projectTasks;
    return projectTasks.filter(t => t.projectId === selectedProjectId);
  }, [projectTasks, selectedProjectId]);

  const selectedProjectMilestones = useMemo(() => {
    if (!selectedProjectId) return milestones;
    return milestones.filter(m => m.projectId === selectedProjectId);
  }, [milestones, selectedProjectId]);

  if (loading) {
    return <LoadingScreen size="large" />;
  }

  return (
    <div className="projects-page">
      <AppHeader />

      {/* Toolbar */}
      <div className="projects-toolbar">
        <div className="toolbar-left">
          {/* Frame Type Toggle */}
          <div className="frame-type-toggle">
            <button
              className={frameTypeFilter === 'all' ? 'active' : ''}
              onClick={() => setFrameTypeFilter('all')}
            >
              All
            </button>
            <button
              className={frameTypeFilter === 'custom' ? 'active' : ''}
              onClick={() => setFrameTypeFilter('custom')}
            >
              <Home size={16} /> Custom
            </button>
            <button
              className={frameTypeFilter === 'kit' ? 'active' : ''}
              onClick={() => setFrameTypeFilter('kit')}
            >
              <Package size={16} /> Kit
            </button>
          </div>
          <select
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(e.target.value || null)}
            className="project-select"
          >
            <option value="">All Projects</option>
            {filteredProjects.map(p => (
              <option key={p.id} value={p.id}>
                {p.frameType === 'kit' ? '[Kit] ' : '[Custom] '}{p.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Status</option>
            <option value="planning">Planning</option>
            <option value="design">Design</option>
            <option value="production">Production</option>
            <option value="installation">Installation</option>
            <option value="completed">Completed</option>
            <option value="on-hold">On Hold</option>
          </select>
        </div>
        <div className="toolbar-center">
          <div className="view-toggle">
            <button 
              className={viewMode === 'board' ? 'active' : ''}
              onClick={() => setViewMode('board')}
            >
              Board
            </button>
            <button 
              className={viewMode === 'gantt' ? 'active' : ''}
              onClick={() => setViewMode('gantt')}
            >
              Gantt
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
        </div>
        <div className="toolbar-right">
          {wonDeals.length > 0 && (
            <button 
              className="convert-btn"
              onClick={() => setShowConvertModal(true)}
            >
              + Convert Deal to Project
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="projects-main">
        {viewMode === 'board' && (
          <ProjectBoard
            projects={filteredProjects}
            tasks={selectedProjectTasks}
            milestones={selectedProjectMilestones}
            selectedProjectId={selectedProjectId}
            userId={user?.id || ''}
          />
        )}
        {viewMode === 'gantt' && (
          <GanttChart
            projects={filteredProjects}
            tasks={selectedProjectTasks}
            milestones={selectedProjectMilestones}
          />
        )}
        {viewMode === 'list' && (
          <div className="projects-list">
            {filteredProjects.length === 0 ? (
              <div className="empty-state">
                <p>No projects found</p>
                {wonDeals.length > 0 && (
                  <button 
                    className="convert-btn"
                    onClick={() => setShowConvertModal(true)}
                  >
                    Convert a Won Deal to Project
                  </button>
                )}
              </div>
            ) : (
              <table className="projects-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Client</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Start Date</th>
                    <th>Target End</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map(project => (
                    <tr key={project.id} onClick={() => setSelectedProjectId(project.id || null)}>
                      <td className="project-name">{project.name}</td>
                      <td>{project.clientName}</td>
                      <td>
                        <span className={`status-badge ${project.status}`}>
                          {project.status}
                        </span>
                      </td>
                      <td>{project.projectType}</td>
                      <td className="project-value">
                        ${project.value.toLocaleString()}
                      </td>
                      <td>{new Date(project.startDate).toLocaleDateString()}</td>
                      <td>{new Date(project.targetEndDate).toLocaleDateString()}</td>
                      <td>
                        <button 
                          className="view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProjectId(project.id || null);
                            setViewMode('board');
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>

      {/* Convert Modal */}
      {showConvertModal && (
        <ConvertToProjectModal
          deals={wonDeals}
          onClose={() => setShowConvertModal(false)}
          userId={user?.id || ''}
        />
      )}
    </div>
  );
};

export default ProjectsPage;
